import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card, useCategoryLabel, CHART_COLORS } from "@/components/common";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { AlertTriangle, Trophy } from "lucide-react";

export default function Analytics() {
  const { t } = useLang();
  const catLabel = useCategoryLabel();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  useEffect(() => {
    api.get("/analytics/dashboard").then((r) => setData(r.data));
    api.get("/analytics/leaderboard").then((r) => setBoard(r.data));
  }, []);

  const pie = (data?.category_progress || []).map((c) => ({ name: catLabel(c.category_code), value: c.attempts }));

  return (
    <div>
      <PageHeader title={t("analytics")} subtitle={t("categoryProgress")} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <h3 className="font-head font-semibold text-slate-900 mb-4">{t("categoryProgress")}</h3>
          {data?.category_progress?.length ? (
            <div className="space-y-4">
              {data.category_progress.map((c) => (
                <div key={c.category_code}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700 font-medium">{catLabel(c.category_code)}</span>
                    <span className="text-slate-500">{c.avg}%</span>
                  </div>
                  <Progress value={c.avg} className="h-2" />
                </div>
              ))}
            </div>
          ) : <p className="text-slate-400 text-sm">{t("noData")}</p>}
        </Card>
        <Card>
          <h3 className="font-head font-semibold text-slate-900 mb-4">{t("categoryDistribution")}</h3>
          {pie.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {pie.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm">{t("noData")}</p>}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" /> Your Mistakes
          </h3>
          {data?.user_mistakes?.length ? (
            <ul className="space-y-2">
              {data.user_mistakes.map((m, i) => (
                <li key={i} className="flex justify-between items-start gap-2 text-sm border-b border-slate-100 pb-2 last:border-0">
                  <span className="text-slate-700">{m.mistake}</span>
                  <span className="shrink-0 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-xs font-medium">{m.count}×</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-emerald-600 text-sm">{t("noMistakes")}</p>}
        </Card>
        <Card>
          <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-500" /> {t("leaderboard")}
          </h3>
          <div className="space-y-2">
            {board.slice(0, 10).map((b, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                <div className="flex items-center gap-2">
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
                  <span className="text-slate-700">{b.name} <span className="text-slate-400 font-mono text-xs">({b.badge_number})</span></span>
                </div>
                <span className="font-medium text-slate-900">{b.avg}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
